<template>
  <v-card outlined>
    <v-card-title>
      {{ $t("user.assistantForm.title") }}
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid">
        <v-card
          class="mb-4"
          v-for="field in filteredAssistant.fields"
          :key="field.fieldId"
          :outlined="field.type !== 'headline'"
          elevation="0"
        >
          <v-card-title :class="field.type === 'headline' ? '' : 'text-subtitle-1'">
            {{ field.question[$i18n.locale] }}
          </v-card-title>

          <v-card-text v-if="field.type !== 'headline'">
            <v-text-field
              v-if="field.type === 'text'"
              v-model="formData[field.fieldId]"
              :rules="field.required ? [(v) => (v !== null && v !== '') || $t('validation.required')] : []"
            />

            <MarkdownEditor
              v-else-if="field.type === 'textarea'"
              v-model="formData[field.fieldId]"
              :rules="field.required ? [(v) => (v !== null && v !== '') || $t('validation.required')] : []"
            />

            <v-select
              v-else-if="field.type === 'select'"
              v-model="formData[field.fieldId]"
              :items="field.options"
              item-value="value"
              :item-text="`answer.${$i18n.locale}`"
              :rules="field.required ? [(v) => (v !== null && v !== '') || $t('validation.required')] : []"
            />

            <div v-else-if="field.type === 'checkbox'">
              <v-checkbox
                v-for="option in field.options"
                v-model="formData[field.fieldId]"
                :key="'checkbox-option-' + option.value"
                :value="option.value"
                :label="option.answer[$i18n.locale]"
                density="compact"
                class="mt-0"
                :rules="
                  field.required
                    ? [
                        (v) =>
                          (Array.isArray(formData[field.fieldId]) && formData[field.fieldId].length > 0) ||
                          $t('validation.required'),
                      ]
                    : []
                "
              />
            </div>

            <v-radio-group
              v-else-if="field.type === 'radio'"
              v-model="formData[field.fieldId]"
              :rules="field.required ? [(v) => (v !== null && v !== '') || $t('validation.required')] : []"
            >
              <v-radio
                v-for="option in field.options"
                :key="'radio-option-' + option.value"
                :value="option.value"
                :label="option.answer[$i18n.locale]"
              />
            </v-radio-group>

            <v-text-field
              v-else-if="field.type === 'number'"
              v-model.number="formData[field.fieldId]"
              type="number"
              :rules="[
                ...(field.required ? [(v) => (v !== null && v !== '') || $t('validation.required')] : []),
                ...(field.min !== undefined
                  ? [(v) => v === null || v === '' || v >= field.min || $t('validation.min', { min: field.min })]
                  : []),
                ...(field.max !== undefined
                  ? [(v) => v === null || v === '' || v <= field.max || $t('validation.max', { max: field.max })]
                  : []),
              ]"
            />
          </v-card-text>
        </v-card>

        <div v-if="requirement.pendingResponse && requirement.pendingResponse.attachments.length" class="pt-2">
          <v-chip
            v-for="attachment in requirement.pendingResponse.attachments"
            :key="attachment.id"
            :href="attachment.file.url"
            small
            class="mr-1 mb-1"
            target="_blank"
            close
            @click:close="removeFile(attachment.id)"
          >
            {{ attachment.file.name }}
          </v-chip>
        </div>

        <v-file-input
          multiple
          :label="$t(`user.responseForm.files.label`)"
          :hint="$t(`user.responseForm.files.hint`)"
          persistent-hint
          v-model="formData.files"
          small-chips
        ></v-file-input>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-btn :loading="loading" @click="saveValid" v-if="valid" color="primary">
        {{ $t("user.assistantForm.saveValid") }}
      </v-btn>

      <v-btn :loading="loading" @click="saveInvalid" v-if="!valid">
        {{ $t("user.assistantForm.saveInvalid") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import assistants from "@fng/common/data/assistants.json";
import { checkWhenConditions } from "@fng/common/checkWhenConditions";
import MarkdownEditor from "@/components/Common/MarkdownEditor.vue";
import UpdateMinimalRequirementMutation from "@/gql/mutations/UpdateMinimalRequirementMutation.gql";
import UpdateStepModelRequirementMutation from "@/gql/mutations/UpdateStepModelRequirementMutation.gql";
import UpdateUserResponseMinimalRequirementMutation from "@/gql/mutations/UpdateUserResponseMinimalRequirementMutation.gql";
import UpdateUserResponseStepModelRequirementMutation from "@/gql/mutations/UpdateUserResponseStepModelRequirementMutation.gql";
import RemoveUserPendingResponseMinimalRequirementMutation from "@/gql/mutations/RemoveUserPendingResponseMinimalRequirementMutation.gql";
import RemoveUserPendingResponseStepModelRequirementMutation from "@/gql/mutations/RemoveUserPendingResponseStepModelRequirementMutation.gql";
import UserRemoveFilePendingResponseMinimalRequirementMutation from "@/gql/mutations/UserRemoveFilePendingResponseMinimalRequirementMutation.gql";
import UserRemoveFilePendingResponseStepModelRequirementMutation from "@/gql/mutations/UserRemoveFilePendingResponseStepModelRequirementMutation.gql";
import AuditorRemoveFilePendingResponseMinimalRequirementMutation from "@/gql/mutations/AuditorRemoveFilePendingResponseMinimalRequirementMutation.gql";
import AuditorRemoveFilePendingResponseStepModelRequirementMutation from "@/gql/mutations/AuditorRemoveFilePendingResponseStepModelRequirementMutation.gql";

export default {
  components: {
    MarkdownEditor,
  },

  props: {
    model: {
      type: String,
      default: "minimal",
    },

    requirement: {
      type: Object,
      required: true,
    },
  },

  data() {
    let formData = {};

    if (this.requirement.requirementId && assistants) {
      const assistant = assistants.find((a) => a.id === this.requirement.requirementId)?.assistant;
      if (assistant?.fields) {
        assistant.fields.forEach((field) => {
          formData[field.fieldId] = field.type === "checkbox" ? [] : null;
        });
      }
    }

    formData = { ...formData, default_text_field: null, ...this.requirement.assistantData };
    formData.files = [];

    return {
      loading: false,
      valid: false,
      formData,
    };
  },

  computed: {
    assistant() {
      const assistant = assistants.find((assistant) => assistant.id === this.requirement.requirementId)?.assistant;

      if (!assistant) return null;

      return {
        fields: [
          ...assistant.fields,
          {
            fieldId: "default_text_field",
            type: "textarea",
            question: {
              en: this.$t("user.assistantForm.defaultTextField.question"),
              de: this.$t("user.assistantForm.defaultTextField.question"),
            },
          },
        ],
      };
    },

    filteredAssistant() {
      return {
        ...this.assistant,
        fields: this.assistant.fields.filter((field) => this.shouldShowField(field)),
      };
    },

    removeFileMutation() {
      const { model } = this;
      const type = "user";

      if (type === "user" && model === "minimal") return UserRemoveFilePendingResponseMinimalRequirementMutation;
      if (type === "user" && model === "step") return UserRemoveFilePendingResponseStepModelRequirementMutation;
      if (type === "auditor" && model === "minimal") return AuditorRemoveFilePendingResponseMinimalRequirementMutation;
      if (type === "auditor" && model === "step") return AuditorRemoveFilePendingResponseStepModelRequirementMutation;

      return null;
    },
  },

  watch: {
    formData: {
      deep: true,
      handler() {
        if (!this.assistant?.fields) return;
        for (const field of this.assistant.fields) {
          if (!field.when) continue;
          if (checkWhenConditions(field.when, this.formData)) continue;
          const val = this.formData[field.fieldId];
          if (field.type === "checkbox") {
            if (Array.isArray(val) && val.length > 0) {
              this.formData[field.fieldId] = [];
            }
          } else if (val !== null && val !== undefined) {
            this.formData[field.fieldId] = null;
          }
        }
      },
    },
  },

  methods: {
    shouldShowField(field) {
      if (!field.when) return true;

      return checkWhenConditions(field.when, this.formData);
    },

    async saveInvalid() {
      this.loading = true;

      try {
        await this.$apollo.mutate({
          mutation: this.model === "minimal" ? UpdateMinimalRequirementMutation : UpdateStepModelRequirementMutation,
          variables: {
            id: this.requirement.id,
            input: {
              assistantData: this.formData,
            },
          },
        });

        await this.$apollo.mutate({
          mutation:
            this.model === "minimal"
              ? RemoveUserPendingResponseMinimalRequirementMutation
              : RemoveUserPendingResponseStepModelRequirementMutation,
          variables: {
            id: this.requirement.id,
          },
        });
      } finally {
        this.formData.files = [];
        this.loading = false;
      }
    },

    async saveValid() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        await this.$apollo.mutate({
          mutation: this.model === "minimal" ? UpdateMinimalRequirementMutation : UpdateStepModelRequirementMutation,
          variables: {
            id: this.requirement.id,
            input: {
              assistantData: this.formData,
            },
          },
        });

        await this.$apollo.mutate({
          mutation:
            this.model === "minimal"
              ? UpdateUserResponseMinimalRequirementMutation
              : UpdateUserResponseStepModelRequirementMutation,
          variables: {
            id: this.requirement.id,
            input: {
              text: generateResponse(this.assistant, this.formData, this.$i18n.locale),
              files: this.formData.files,
            },
          },
        });
      } finally {
        this.formData.files = [];
        this.loading = false;
      }
    },

    async removeFile(id) {
      await this.$apollo.mutate({
        mutation: this.removeFileMutation,
        variables: {
          id: this.requirement.id,
          attachmentId: id,
        },
      });
    },
  },
};

function generateResponse(assistant, formData, locale) {
  if (!assistant?.fields) return "";

  const responses = assistant.fields.map((field) => {
    // Check when conditions first
    if (field.when && !checkWhenConditions(field.when, formData)) {
      return "";
    }

    let answer = "";

    if (field.type === "checkbox") {
      const selectedOptions = field.options.filter((opt) => formData[field.fieldId].includes(opt.value));
      if (selectedOptions.length) {
        const answers = selectedOptions.map((opt) => `- ${opt.answer[locale]}`);
        answer = `\n${answers.join("\n\n")}`;
      }
    } else if (["text", "textarea", "number"].includes(field.type) && formData[field.fieldId] !== null) {
      answer = `\n${formData[field.fieldId].toString()}`;
    } else if (["radio", "select"].includes(field.type) && formData[field.fieldId]) {
      const selectedOption = field.options.find((opt) => opt.value === formData[field.fieldId]);
      if (selectedOption) {
        answer = `\n${selectedOption.answer[locale]}`;
      }
    }

    return answer ? `**${field.question[locale]}**\n\n${answer}` : "";
  });

  return responses.filter((r) => r).join("\n\n");
}
</script>
